import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { Button, Input, Checkbox, Image, Divider } from '@nextui-org/react'
import { Icon } from '@iconify/react'
import api from 'src/lib/api'
import React, { useCallback, useState } from 'react'
import Mixpanel from 'src/lib/mixpanel'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { AxiosError } from 'axios'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useWorkspaceContextData } from 'src/context/workspace'

export default function Login() {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false)

  const [isVisible, setIsVisible] = React.useState(false)
  const [reCaptchaToken, setRecaptchaToken] = useState<string>('')
  const handleRecaptchaVerify = useCallback((token) => {
    setRecaptchaToken(token)
  }, [])
  const toggleVisibility = () => setIsVisible(!isVisible)
  const navigate = useNavigate()
  const { loadUser } = useWorkspaceContextData()
  const login = async (e: any) => {
    e.preventDefault()
    try {
      const res = await api.post('/auth/signin', { email, password, token: reCaptchaToken })

      if (res.data?.accessToken && res.data?.refreshToken) {
        localStorage.setItem('accessToken', res.data.accessToken)
        localStorage.setItem('refreshToken', res.data.refreshToken)
        localStorage.setItem('email', res.data.email)

        // ✅ Only track login, do NOT call Mixpanel.signup here
        Mixpanel.login(res.data.uuid)

        // ✅ Ensure tracking completes before navigating
        await Promise.all([
          Mixpanel.track('User logged in', { email: email }),
          new Promise((resolve) => {
            if (window.gtag) {
              window.gtag('event', 'login', { user_id: res.data.uuid })
              setTimeout(resolve, 500) // ✅ Ensure GA4 has time to process
            } else {
              resolve()
            }
          }),
        ])
        await loadUser?.()
        if (res.data.firstname === '' && res.data.lastname === '') {
          navigate('/settings')
        } else {
          navigate('/')
        }
      } else {
        setRefreshReCaptcha(!refreshReCaptcha)
        setError('Login was not successful. Try again.')
        setTimeout(() => setError(''), 3000)
        // Track login failure
        await Mixpanel.track('Login failed', {
          email: email,
          error_message: 'No access or refresh token returned',
        })
      }
    } catch (err) {
      setRefreshReCaptcha(!refreshReCaptcha)
      const error = err as AxiosError
      let message = 'Login was not successful.'
      if (error instanceof AxiosError) {
        message = error.response?.data?.message || message
      } else {
        message += ' ' + err.message
      }
      setError(message)

      // Track login failure with detailed error
      await Mixpanel.track('Login failed', {
        email: email,
        error_message: message,
      })

      setTimeout(() => setError(''), 3000)
    }
  }

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      )
  }
  const [loading, setLoading] = useState<boolean>(false)
  const loginWithGoogle = async () => {
    setLoading(true)
    const res = await api.get('/auth/google/get-url')
    if (res.data?.authUrl) {
      window.location = res.data.authUrl
    }
    setLoading(false)
  }
  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>

      <div className="flex h-full w-full flex-col items-center justify-center px-3">
        <div className="flex flex-col items-center pb-6">
          <Image
            src="/img/powerdocs-logo.png"
            alt="powerdocs logo"
            key={'logo'}
            width={200}
            radius="none"
            className="mb-6"
          />
          <p className="text-xl font-medium">Welcome back</p>
          <p className="text-small text-default-500">Log in to your account to continue</p>
        </div>
        <div className="mt-2 flex w-full max-w-md flex-col gap-4 rounded-large bg-content1 px-8 py-6 shadow-small">
          <div className="flex flex-col gap-2">
            <Button
              isDisabled={loading}
              startContent={<Icon icon="logos:google-icon" width={24} />}
              variant="bordered"
              onClick={loginWithGoogle}
            >
              Continue with Google
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <Divider className="flex-1" />
            <p className="shrink-0 text-tiny text-default-500">OR</p>
            <Divider className="flex-1" />
          </div>
          <form className="flex flex-col gap-2" onSubmit={(e) => e.preventDefault()}>
            <Input
              label="Email address"
              name="email"
              placeholder="Enter your email"
              type="email"
              variant="bordered"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
            />
            <Input
              endContent={
                <button type="button" onClick={toggleVisibility}>
                  {isVisible ? (
                    <Icon className="pointer-events-none text-2xl text-default-400" icon="solar:eye-closed-linear" />
                  ) : (
                    <Icon className="pointer-events-none text-2xl text-default-400" icon="solar:eye-bold" />
                  )}
                </button>
              }
              label="Password"
              name="password"
              placeholder="Enter your password"
              type={isVisible ? 'text' : 'password'}
              variant="bordered"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <GoogleReCaptcha onVerify={handleRecaptchaVerify} refreshReCaptcha={refreshReCaptcha} />
            <div className="flex items-center justify-between px-1 py-2 text-small">
              <Checkbox name="remember" size="sm">
                Remember me
              </Checkbox>
              <RouterLink className="text-default-500" to="/account/reset">
                Forgot password?
              </RouterLink>
            </div>
            <Button
              color="secondary"
              type="submit"
              onClick={login}
              isDisabled={email.length === 0 || (email !== 'demo@powerdocs.so' && password.length < 5) || !validateEmail(email) || loading}
            >
              Log in
            </Button>
            {error && <p className={'text-danger text-center text-sm'}>{error}</p>}
          </form>
          <p className="text-center text-small">
            Need to create an account?&nbsp;
            <RouterLink to="/signup">Sign up</RouterLink>
          </p>
        </div>
      </div>
    </>
  )
}
